/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import { CheckUsageDto } from '../common/check-usage.dto';
import { CheckUsageMapper } from '../common/check-usage.mapper';
import {
  JobSpecTemplateDetailsDto,
  JobSpecTemplateFilterDto,
  JobSpecTemplateListItemDto,
} from './job-spec-template.dto';
import {
  JobSpecTemplateDetailsMapper,
  JobSpecTemplateListItemMapper,
} from './job-spec-template.mapper';

@Injectable()
export class JobSpecTemplateRestService extends AbstractRestService {

  constructor(settings: RestSettings) {
    super(settings);
  }

  create(template: JobSpecTemplateDetailsDto): Observable<JobSpecTemplateDetailsDto> {
    return this.httpPost(
      `${this.bp}/job-specification-templates`,
      template,
      JobSpecTemplateDetailsMapper);
  }

  update(template: JobSpecTemplateDetailsDto): Observable<JobSpecTemplateDetailsDto> {
    return this.httpPut(
      `${this.bp}/job-specification-templates/${template.specTemplateId}`,
      template,
      JobSpecTemplateDetailsMapper);
  }

  get(id: ID): Observable<JobSpecTemplateDetailsDto> {
    return this.httpGet(`${this.bp}/job-specification-templates/${id}`, JobSpecTemplateDetailsMapper);
  }

  findAllAsDataSource(filter: JobSpecTemplateFilterDto): SlicedDataSource<JobSpecTemplateListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/job-specification-templates`,
      filter,
      identity(),
      arrayOf(JobSpecTemplateListItemMapper));
  }

  findAllAsFinderDataSource(filter: JobSpecTemplateFilterDto): SlicedDataSource<JobSpecTemplateListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/job-specification-templates/finder`,
      filter,
      identity(),
      arrayOf(JobSpecTemplateListItemMapper));
  }

  undelete(template: JobSpecTemplateListItemDto): Observable<JobSpecTemplateListItemDto> {
    return this.httpPut(
      `${this.bp}/job-specification-templates/undelete/${template.id}`,
      void 0,
      optimisticLockingOf(JobSpecTemplateListItemMapper));
  }

  checkUsage(template: JobSpecTemplateListItemDto): Observable<CheckUsageDto> {
    const { id, version } = template;
    return this.httpGet(
      `${this.bp}/job-specification-templates/check-usage/${id}`,
      { version },
      void 0,
      CheckUsageMapper,
    );
  }

  deactivateOrDelete(template: JobSpecTemplateListItemDto, checkUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/job-specification-templates/${template.id}`, checkUsage, CheckUsageMapper);
  }
}
